/**
 * GarmentMerchandiserHome — the action-first garment home for the Merchandiser.
 *
 * The merchandiser acts on Style Files; this is their work queue, drawn from the
 * same deterministic risk groups the Owner home summarises, but framed as tasks
 * (Quality / Timeline / Production / Costing) ordered by urgency. A small stat
 * row gives the day's shape. One read: `orderBook.overview`.
 *
 * @see prototype/garment/home-simple.html — the Merchandiser (Priya) variant
 */
import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { Card, CardBody, cn } from "@flint/ui";
import type { orderBook as contract } from "@flint/api-contract";
import { trpc } from "../../../lib/trpc.js";

type OverviewData = contract.OverviewOutput;
type Row = OverviewData["behindSchedule"][number];

const intIN = (n: number) => n.toLocaleString("en-IN");

type Tag = "Quality" | "Timeline" | "Production" | "Costing";
const TAG_STYLE: Record<Tag, string> = {
  Quality: "bg-bad-50 text-bad",
  Timeline: "bg-warn-50 text-warn",
  Production: "bg-slate-100 text-slate-600",
  Costing: "bg-sky-50 text-sky-700",
};

interface Task {
  tag: Tag;
  styleId: string;
  title: string;
  sub: string;
  rank: number; // lower = more urgent
}

export function GarmentMerchandiserHome() {
  const overviewQ = trpc.orderBook.overview.useQuery({});
  const meQ = trpc.identity.me.useQuery({});
  const firstName = (meQ.data?.user?.name ?? "").split(/\s+/)[0] ?? "";

  if (overviewQ.isLoading) {
    return (
      <Card>
        <CardBody>
          <p className="text-sm text-muted">Loading your queue…</p>
        </CardBody>
      </Card>
    );
  }
  if (overviewQ.error || !overviewQ.data) {
    return (
      <Card className="border-bad">
        <CardBody>
          <p className="text-bad font-medium">Unable to load your queue.</p>
          <p className="text-sm text-muted mt-2">{overviewQ.error?.message}</p>
        </CardBody>
      </Card>
    );
  }

  const data = overviewQ.data;
  const tasks = buildQueue(data);

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">
          Hi{firstName ? ` ${firstName}` : ""} — here&rsquo;s your queue.
        </h1>
        <p className="text-muted text-sm mt-1">
          The orders waiting on you, in the order they&rsquo;ll bite. Tap one to open its style file.
        </p>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat label="Open style files" value={intIN(data.summary.openOrders)} />
        <Stat label="Shipping this week" value={intIN(data.summary.shippingThisWeek)} />
        <Stat label="Tasks waiting on you" value={intIN(tasks.length)} tone={tasks.length > 0 ? "bad" : "ink"} />
      </div>

      <section>
        <h2 className="text-base font-bold mb-3">Your action queue</h2>
        {tasks.length === 0 ? (
          <Card className="border-emerald-100 bg-emerald-50">
            <CardBody>
              <p className="text-sm text-ink-2">
                Nothing&rsquo;s waiting on you — every order is on plan. Nice.
              </p>
            </CardBody>
          </Card>
        ) : (
          <div className="space-y-2.5">
            {tasks.map((t) => (
              <Link
                key={`${t.tag}-${t.styleId}`}
                to="/merchandising/styles/$styleId/overview"
                params={{ styleId: t.styleId }}
                className="flex items-center gap-3.5 rounded-md border border-line bg-paper p-3.5 shadow-sm transition-colors hover:border-sky-300"
              >
                <span
                  className={cn(
                    "w-24 shrink-0 rounded-md py-[3px] text-center text-xs font-bold uppercase tracking-wide",
                    TAG_STYLE[t.tag],
                  )}
                >
                  {t.tag}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold">{t.title}</span>
                  <span className="block text-xs text-muted">{t.sub}</span>
                </span>
                <ChevronRight size={16} className="shrink-0 text-muted" />
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * Turn the risk groups into the merchandiser's task list. Quality blocks and
 * schedule slips bite first, then stalled production, then thin margins.
 */
function buildQueue(data: OverviewData): Task[] {
  const tasks: Task[] = [];
  for (const r of data.qc as Row[]) {
    const blocked = r.severity === "high";
    tasks.push({
      tag: "Quality",
      styleId: r.styleId,
      title: blocked
        ? `Re-inspect ${r.name} — batch failed`
        : `Record inspection — ${r.name}`,
      sub: `${r.buyerName}${r.orderQty > 0 ? ` · ${intIN(r.orderQty)} pcs` : ""} · ${r.note}`,
      rank: blocked ? 0 : 2,
    });
  }
  for (const r of data.behindSchedule as Row[]) {
    tasks.push({
      tag: "Timeline",
      styleId: r.styleId,
      title: `Re-plan ${r.name} timeline`,
      sub: `${r.buyerName} · ${r.note}`,
      rank: r.severity === "high" ? 1 : 3,
    });
  }
  for (const r of data.behindPlan as Row[]) {
    tasks.push({
      tag: "Production",
      styleId: r.styleId,
      title: `Log output — ${r.name}`,
      sub: `${r.buyerName} · ${r.note}`,
      rank: 4,
    });
  }
  for (const r of data.lowMargin as Row[]) {
    tasks.push({
      tag: "Costing",
      styleId: r.styleId,
      title: `Review costing — ${r.name}`,
      sub: `${r.buyerName} · ${r.note}`,
      rank: 5,
    });
  }
  return tasks.sort((a, b) => a.rank - b.rank);
}

function Stat({ label, value, tone = "ink" }: { label: string; value: string; tone?: "bad" | "ink" }) {
  return (
    <Card>
      <CardBody className="px-4 py-3.5">
        <div
          className={cn("text-2xl font-bold tracking-tight tabular-nums", tone === "bad" && "text-bad")}
        >
          {value}
        </div>
        <div className="text-xs text-muted mt-0.5">{label}</div>
      </CardBody>
    </Card>
  );
}
